/**
 * components/Bar/StackedBar
 * ──────────────────────────────────────────────────────────────────────────
 * The 9px segmented bar over a #F2F2F2 track, 999px radius: "Where the cash
 * sits" (split by scope) and "Count results this period" (matched / short /
 * over / waived).
 *
 * Two details that matter:
 *  - Segment widths are computed as percentages of the total, and the total is
 *    passed in rather than summed here, because the caller is the only one that
 *    knows the figures share a currency (invariant 4). A bar that silently
 *    added AED to INR would look perfectly reasonable.
 *  - A segment with a non-zero value never renders thinner than 2px. A row that
 *    exists but is invisible reads as a bug in the data, not as a small number.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';

export interface BarSegment {
  key: string;
  /** Any comparable magnitude — a count, or minor units already narrowed. */
  value: number;
  color: string;
}

export interface StackedBarProps {
  segments: readonly BarSegment[];
  /** Bar thickness: 9px on the charts, 6px on a purpose progress bar. */
  height?: number;
}

/** Non-zero values are floored at this share so they stay visible. */
const MIN_VISIBLE_PERCENT = 2;

export function StackedBar({ segments, height = 9 }: StackedBarProps) {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);

  return (
    <View style={[styles.track, { height, borderRadius: radii.pill }]}>
      {total > 0 &&
        segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <View
              key={segment.key}
              style={{
                width: `${Math.max(MIN_VISIBLE_PERCENT, (segment.value / total) * 100)}%`,
                backgroundColor: segment.color,
              }}
            />
          ))}
    </View>
  );
}

const styles = StyleSheet.create({
  track: {
    flexDirection: 'row',
    width: '100%',
    backgroundColor: neutral.ruleLight,
    // Clips the segments to the pill shape; without it the first and last
    // segments square off the ends.
    overflow: 'hidden',
  },
});

export default StackedBar;
